import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { API_BASE } from "../api/config";
import LocationSelect from "../components/LocationSelect";
import "./CashClosing.css";

const SHOPS = [
  "ALL",
  "GMP",
  "OSHT",
  "OSPF",
  "MT",
  "JS",
  "JCHT",
  "JCPF",
  "MDYHT",
  "MDYPF",
];

const ENTRY_SHOPS = SHOPS.filter((s) => s !== "ALL");

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString();
}

export default function CashClosing() {
  const [date, setDate] = useState(todayKey());
  const [shop, setShop] = useState("ALL");
  const [rows, setRows] = useState([]);
  const [showAllRows, setShowAllRows] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [form, setForm] = useState({
    shopCode: "MDYHT",
    closingDate: todayKey(),
    openingCash: "",
    actualCash: "",
    note: "",
  });

  function updateForm(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  async function loadClosing() {
    try {
      setLoading(true);
      setError("");
      setSuccessMsg("");

      const res = await fetch(
        `${API_BASE}/api/erp/daily-cash-closing?shop=${shop}&date=${date}`,
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to load daily closing");
      }

      setRows(data.rows || []);
    } catch (err) {
      setError(err.message || "Daily closing loading failed");
    } finally {
      setLoading(false);
    }
  }

  function getSelectedRow() {
    return rows.find((row) => row.shop_code === form.shopCode);
  }

  function getExpectedCashForForm() {
    const row = getSelectedRow();

    const openingCash = Number(form.openingCash || 0);
    const cashIn = Number(row?.cash_in || 0);
    const cashOut = Number(row?.cash_out || 0);

    return openingCash + cashIn - cashOut;
  }

  function useExpectedCash() {
    const expectedCash = getExpectedCashForForm();

    setForm((prev) => ({
      ...prev,
      actualCash: String(expectedCash),
    }));

    setSuccessMsg(`Expected cash ${formatMoney(expectedCash)} Ks copied to Actual Cash.`);
  }

  function selectRow(row) {
    setForm({
      shopCode: row.shop_code,
      closingDate: date,
      openingCash: String(Number(row.opening_cash || 0)),
      actualCash: String(Number(row.actual_cash || 0)),
      note: row.note || "",
    });

    setSuccessMsg(`Selected ${row.shop_code} for closing.`);
  }

  async function saveClosing(e) {
    e.preventDefault();

    try {
      setSaving(true);
      setError("");
      setSuccessMsg("");

      if (!form.shopCode) {
        throw new Error("Shop required");
      }

      const payload = {
        shopCode: form.shopCode,
        closingDate: form.closingDate || date,
        openingCash: Number(form.openingCash || 0),
        actualCash: Number(form.actualCash || 0),
        note: form.note || "",
        createdBy: "admin",
      };

      const res = await fetch(`${API_BASE}/api/erp/daily-cash-closing`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save daily closing");
      }

      setSuccessMsg(`${form.shopCode} daily cash closing saved.`);
      await loadClosing();
    } catch (err) {
      setError(err.message || "Daily closing save failed");
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    loadClosing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop, date]);

  const displayRows = useMemo(() => {
    if (showAllRows) return rows;

    return rows.filter((row) => {
      const hasActivity =
        Number(row.cash_in || 0) !== 0 ||
        Number(row.cash_out || 0) !== 0 ||
        Number(row.expected_cash || 0) !== 0 ||
        Number(row.actual_cash || 0) !== 0 ||
        Number(row.difference || 0) !== 0;

      return hasActivity || row.status !== "OPEN";
    });
  }, [rows, showAllRows]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.cashIn += Number(row.cash_in || 0);
        acc.cashOut += Number(row.cash_out || 0);
        acc.expected += Number(row.expected_cash || 0);
        acc.actual += Number(row.actual_cash || 0);
        acc.difference += Number(row.difference || 0);
        return acc;
      },
      {
        cashIn: 0,
        cashOut: 0,
        expected: 0,
        actual: 0,
        difference: 0,
      },
    );
  }, [rows]);

  return (
    <Layout title="Daily Cash Closing">
      <div className="cashClosing">
        <div className="cashClosing__hero">
          <div>
            <div className="cashClosing__eyebrow">ERP Finance Control</div>
            <h1>Daily Cash Closing</h1>
            <p>
              Check each shop cash in, cash out, expected cash, actual cash and
              closing difference.
            </p>
          </div>

          <div className="cashClosing__filters">
            <input
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                updateForm("closingDate", e.target.value);
              }}
            />

            <LocationSelect
              value={shop}
              onChange={setShop}
              type="SHOP"
              includeAll={true}
              allLabel="All Shops"
            />

            <button onClick={loadClosing} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {error && <div className="cashClosing__error">{error}</div>}
        {successMsg && <div className="cashClosing__success">{successMsg}</div>}

        <div className="cashClosing__cards">
          <div className="closingCard">
            <span>Total Cash In</span>
            <strong>{formatMoney(totals.cashIn)} Ks</strong>
            <small>Today company received money</small>
          </div>

          <div className="closingCard">
            <span>Total Cash Out</span>
            <strong>{formatMoney(totals.cashOut)} Ks</strong>
            <small>Expense / refund / transfer out</small>
          </div>

          <div className="closingCard">
            <span>Expected Cash</span>
            <strong>{formatMoney(totals.expected)} Ks</strong>
            <small>Opening + Cash In - Cash Out</small>
          </div>

          <div className="closingCard">
            <span>Difference</span>
            <strong>{formatMoney(totals.difference)} Ks</strong>
            <small>Actual Cash - Expected Cash</small>
          </div>
        </div>

        <div className="cashClosing__grid">
          <div className="closingPanel">
            <h3>Save Daily Closing</h3>

            <form className="closingForm" onSubmit={saveClosing}>
              <div className="closingForm__row">
                <label>
                  Shop
                  <select
                    value={form.shopCode}
                    onChange={(e) => updateForm("shopCode", e.target.value)}
                  >
                    {ENTRY_SHOPS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Date
                  <input
                    type="date"
                    value={form.closingDate}
                    onChange={(e) => updateForm("closingDate", e.target.value)}
                  />
                </label>
              </div>

              <div className="closingForm__row">
                <label>
                  Opening Cash
                  <input
                    type="number"
                    value={form.openingCash}
                    onChange={(e) => updateForm("openingCash", e.target.value)}
                    placeholder="Enter opening cash"
                  />
                </label>

                <label>
                  Actual Cash
                  <div className="closingActualCashBox">
                    <input
                      type="number"
                      value={form.actualCash}
                      onChange={(e) => updateForm("actualCash", e.target.value)}
                      placeholder="Enter counted cash"
                    />
                    <button
                      type="button"
                      className="closingMiniBtn"
                      onClick={useExpectedCash}
                    >
                      Use Expected
                    </button>
                  </div>
                </label>
              </div>

              <label>
                Note
                <textarea
                  value={form.note}
                  onChange={(e) => updateForm("note", e.target.value)}
                  placeholder="Example: closing checked by cashier"
                />
              </label>

              <button type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save Daily Closing"}
              </button>
            </form>
          </div>

          <div className="closingPanel">
            <h3>Closing Formula</h3>

            <div className="closingFormula">
              <div>
                <span>Expected Cash</span>
                <strong>Opening Cash + Cash In - Cash Out</strong>
              </div>

              <div>
                <span>Difference</span>
                <strong>Actual Cash - Expected Cash</strong>
              </div>

              <div>
                <span>BALANCED</span>
                <strong>Difference = 0</strong>
              </div>

              <div>
                <span>DIFFERENCE</span>
                <strong>Need cashier/admin review</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="closingPanel closingPanel--table">
          <div className="closingPanel__tableHead">
            <div>
              <h3>Daily Closing List</h3>
              <p>Click a row to fill the closing form quickly.</p>
            </div>

            <button
              type="button"
              className="closingToggleBtn"
              onClick={() => setShowAllRows((prev) => !prev)}
            >
              {showAllRows ? "Active Only" : "Show All Shops"}
            </button>
            <div style={{ display: "none" }}>
            </div>
          </div>

          <div className="closingTableWrap">
            <table className="closingTable">
              <thead>
                <tr>
                  <th>Shop</th>
                  <th>Opening</th>
                  <th>Cash In</th>
                  <th>Cash Out</th>
                  <th>Expected</th>
                  <th>Actual</th>
                  <th>Difference</th>
                  <th>Status</th>
                  <th>Note</th>
                  <th>Updated</th>
                </tr>
              </thead>

              <tbody>
                {displayRows.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="closingTable__empty">
                      No daily closing data.
                    </td>
                  </tr>
                ) : (
                  displayRows.map((row) => (
                    <tr key={row.shop_code} onClick={() => selectRow(row)}>
                      <td>{row.shop_code}</td>
                      <td>{formatMoney(row.opening_cash)} Ks</td>
                      <td>{formatMoney(row.cash_in)} Ks</td>
                      <td>{formatMoney(row.cash_out)} Ks</td>
                      <td>{formatMoney(row.expected_cash)} Ks</td>
                      <td>{formatMoney(row.actual_cash)} Ks</td>
                      <td>{formatMoney(row.difference)} Ks</td>
                      <td>
                        <span
                          className={
                            row.status === "BALANCED"
                              ? "closingBadge closingBadge--ok"
                              : row.status === "DIFFERENCE"
                                ? "closingBadge closingBadge--bad"
                                : "closingBadge closingBadge--open"
                          }
                        >
                          {row.status}
                        </span>
                      </td>
                      <td>{row.note || "-"}</td>
                      <td>{String(row.updated_at || "").slice(0, 19) || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}




